import React, { useState, useEffect, useRef } from 'react';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import axios from 'axios';
// 1. Theme Configuration matching your custom tokens
const theme = {
  colors: {
    primary: '#003ec7',
    primaryContainer: '#0052ff',
    error: '#ba1a1a',
    errorContainer: '#ffdad6',
    background: '#f7f9fb',
    surface: '#f7f9fb',
    surfaceContainerLowest: '#ffffff',
    surfaceContainerLow: '#f2f4f6',
    surfaceContainer: '#eceef0',
    surfaceContainerHigh: '#e6e8ea',
    surfaceContainerHighest: '#e0e3e5',
    onSurface: '#191c1e',
    onSurfaceVariant: '#434656',
    outlineVariant: '#c3c5d9',
    white: '#ffffff',
  },
  borderRadius: {
    default: '0.125rem',
    lg: '0.25rem',
    xl: '0.5rem',
    full: '0.75rem',
  },
  spacing: {
    base: '4px',
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    panelGap: '2px',
  },
  fontFamily: {
    body: "'Inter', sans-serif",
    headline: "'Hanken Grotesk', sans-serif",
  }
};

// 2. Global Styles injection for Typography & Layout resets
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=block');
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600&family=Hanken+Grotesk:wght@600;700;800&display=swap');

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  body {
    background-color: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.onSurface};
    font-family: ${props => props.theme.colors.body};
    font-size: 14px;
    line-height: 20px;
    overflow: hidden;
  }
`;

// 3. Styled Primitives Component Architecture
const RootWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 80vh;
  overflow: hidden;
`;

const Header = styled.header`
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 50;
  background-color: ${props => props.theme.colors.surface};
  border-bottom: 1px solid ${props => props.theme.colors.outlineVariant};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${props => props.theme.spacing.md};
  height: 64px;
  flex-shrink: 0;
`;

const FlexGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.propsGap || props.theme.spacing.md};
`;

const IconButton = styled.span`
  font-family: 'Material Symbols Outlined';
  color: ${props => props.color || props.theme.colors.onSurfaceVariant};
  cursor: pointer;
  padding: ${props => props.theme.spacing.base};
  border-radius: ${props => props.theme.borderRadius.xl};
  transition: background-color 0.2s, opacity 0.1s;
  user-select: none;

  &:hover {
    background-color: ${props => props.theme.colors.surfaceContainerHigh};
  }
  &:active {
    opacity: 0.7;
  }
`;

const MainTitle = styled.h1`
  font-family: ${props => props.theme.fontFamily.headline};
  font-size: 20px;
  line-height: 28px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${props => props.theme.colors.primary};
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownMenu = styled.div`
  position: absolute;
  right: 0;
  margin-top: 8px;
  width: 192px;
  border-radius: ${props => props.theme.borderRadius.xl};
  background-color: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.colors.outlineVariant};
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
  z-index: 50;
  overflow: hidden;
  padding: 4px 0;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background: none;
  border: none;
  text-align: left;
  font-size: 14px;
  font-family: ${props => props.theme.fontFamily.body};
  cursor: pointer;
  transition: background-color 0.2s;
  color: ${props => props.isError ? props.theme.colors.error : props.theme.colors.onSurface};

  &:hover {
    background-color: ${props => props.isError ? 'rgba(186, 26, 26, 0.05)' : props.theme.colors.surfaceContainerLow};
  }

  .icon {
    margin-right: ${props => props.theme.spacing.sm};
    font-size: 20px;
  }
`;

const MainContent = styled.main`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: ${props => props.theme.spacing.md};
  gap: ${props => props.theme.spacing.panelGap};
  overflow: hidden;

  @media (min-width: 768px) {
    flex-direction: row;
    height: calc(100vh - 64px);
  }
`;

const Panel = styled.section`
  flex: 1;
  background-color: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.colors.outlineVariant};
  border-radius: ${props => props.theme.borderRadius.xl};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 50vh; 
`;

const PanelHeader = styled.div`
  padding: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.outlineVariant};
  background-color: ${props => props.backgroundColor || props.theme.colors.surfaceContainerLowest};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const PanelTitle = styled.h2`
  font-family: ${props => props.theme.fontFamily.headline};
  font-size: 20px;
  line-height: 28px;
  font-weight: 600;
  color: ${props => props.theme.colors.onSurface};
`;

const Badge = styled.span`
  background-color: ${props => props.backgroundColor || props.theme.colors.surfaceContainer};
  color: ${props => props.color || props.theme.colors.onSurfaceVariant};
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  padding: ${props => props.theme.spacing.base} ${props => props.isPill ? props.theme.spacing.md : props.theme.spacing.base};
  border-radius: ${props => props.isPill ? props.theme.borderRadius.full : props.theme.borderRadius.xl};
`;

const ItemList = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding: ${props => props.theme.spacing.sm};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const RowCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.xl};
  border: 1px solid ${props => props.isActive ? 'rgba(0, 62, 199, 0.2)' : props.theme.colors.outlineVariant};
  background-color: ${props => props.isActive ? 'rgba(0, 76, 237, 0.05)' : props.theme.colors.surfaceContainerLow};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: default;
  flex-shrink: 0;

  &:hover {
    background-color: ${props => props.theme.colors.surfaceContainerHigh};
  }
  &:active {
    transform: scale(0.98);
  }
`;

const TextBlock = styled.div`
  .title {
    font-size: 14px;
    font-weight: 600;
    color: ${props => props.theme.colors.onSurface};
  }
  .subtitle {
    font-size: 12px;
    color: ${props => props.theme.colors.onSurfaceVariant};
  }
`;

const ActionButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${props => props.theme.borderRadius.full};
  border: none;
  cursor: pointer;
  color: ${props => props.theme.colors.white};
  background-color: ${props => props.isRemove ? props.theme.colors.error : props.theme.colors.primary};
  transition: background-color 0.2s;
  flex-shrink: 0;

  &:hover {
    background-color: ${props => props.isRemove ? '#93000a' : props.theme.colors.primaryContainer};
  }

  span {
    font-size: 18px;
  }
`;

const EmptyState = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: ${props => props.theme.colors.onSurfaceVariant};
  padding: ${props => props.theme.spacing.xl} 0;
  opacity: 0.6;

  span {
    font-size: 48px;
    margin-bottom: ${props => props.theme.spacing.sm};
  }
`;

const FooterPanel = styled.div`
  padding: ${props => props.theme.spacing.md};
  border-top: 1px solid ${props => props.theme.colors.outlineVariant};
  background-color: ${props => props.theme.colors.surfaceContainerLowest};
  flex-shrink: 0;
  max-height:20vh;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: ${props => props.theme.spacing.md} 0;
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.xl};
  font-weight: bold;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
  transition: background-color 0.2s, transform 0.1s;

  &:hover {
    background-color: ${props => props.theme.colors.primaryContainer};
  }
  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(25, 28, 30, 0.4);
  backdrop-filter: blur(4px);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.md};
`;

const ModalCard = styled.div`
  background-color: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing.xl};
  max-width: 384px;
  width: 100%;
  text-align: center;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const SuccessIconWrapper = styled.div`
  width: 64px;
  height: 64px;
  background-color: rgba(0, 82, 255, 0.1);
  color: ${props => props.theme.colors.primaryContainer};
  border-radius: ${props => props.theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${props => props.theme.spacing.md};

  span {
    font-size: 32px;
  }
`;

const INITIAL_ITEMS = [
  { id: '4402', name: 'Row Item 1', image: null },
  { id: '4403', name: 'Row Item 2', image: null },
  { id: '4404', name: 'Row Item 3', image: null },
  { id: '4405', name: 'Row Item 4', image: null },
];

const runTeams = () => {
  return axios({
    method: 'post',
    url: `https://sx7vpiy3ij.execute-api.us-east-2.amazonaws.com/admin_dev/team-pull`,
    withCredentials: false,
    data: {
      option: 'grab'
    },
  })
    .then((res) => {
      return res.data;
    })
    .catch((err) => {
      console.error(err);
      throw err;
    });
};

const writeTeams = (selected) => {
  const safeSelected = Array.isArray(selected) ? selected : [];
  const itemNames = safeSelected.map(item => item.name);

  return axios({
    method: 'post',
    url: `https://sx7vpiy3ij.execute-api.us-east-2.amazonaws.com/admin_dev/team-pull`,
    withCredentials: false,
    data: {
      option: 'write',
      files: itemNames ?? []
    },
  })
    .then((res) => {
      return res.data;
    })
    .catch((err) => {
      console.error(err);
      throw err;
    });
};

// --- HELPER FUNCTIONS FOR SORTING ---
function extractTeamDate(filename) {
  const match = String(filename).match(/(\d{2})_(\d{2})(?:\.csv)?$/);
  if (!match) return null;
  const [, month, day] = match;
  return new Date(2000, parseInt(month, 10) - 1, parseInt(day, 10));
}

function formatFileList(fileList) {
  const sorted = [...fileList].sort((a, b) => {
    const dateA = extractTeamDate(a);
    const dateB = extractTeamDate(b);
    if (!dateA || !dateB) return String(b).localeCompare(String(a));
    return dateB - dateA; // Descending order (newest dates first)
  });

  return sorted.map((filename, index) => ({
    id: index + 1,
    name: filename
  }));
}

export default function Config() {
  const [availableItems, setAvailableItems] = useState(INITIAL_ITEMS);
  const [selectedItems, setSelectedItems] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const dropdownRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    setLoading(true);
    runTeams()
      .then((data) => {
        if (!isMounted) return;
        data = data.data;

        const items = Array.isArray(data) ? data : (data?.items ?? []);
        let ordered_items = formatFileList(items);
        setAvailableItems(ordered_items);
        setError(null);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError('Failed to load items.');
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      isMounted = false;
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selectItem = (item) => {
    setAvailableItems(prev => prev.filter(i => i.id !== item.id));
    setSelectedItems(prev => [...prev, item]);
  };

  const deselectItem = (item) => {
    setSelectedItems(prev => prev.filter(i => i.id !== item.id));
    setAvailableItems(prev => [...prev, item]);
  };

  const handleSubmit = () => {
    setSubmitting(true);
    writeTeams(selectedItems)
      .then(() => {
        setShowSuccess(true);
      })
      .catch(() => {
        setError('Submission failed.');
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <RootWrapper>
        
        {/* TopAppBar */}
        <Header>
          <FlexGroup>
            <IconButton className="material-symbols-outlined" color={theme.colors.primary}>menu</IconButton>
            <MainTitle>Item Selection</MainTitle>
          </FlexGroup>
          
          <DropdownContainer ref={dropdownRef}>
            <IconButton 
              className="material-symbols-outlined" 
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              more_vert
            </IconButton>
            
            {dropdownOpen && (
              <DropdownMenu>
                <DropdownItem onClick={() => alert('Add action triggered!')}>
                  <span className="material-symbols-outlined icon">add</span> Add
                </DropdownItem>
                <DropdownItem isError onClick={() => alert('Delete action triggered!')}>
                  <span className="material-symbols-outlined icon">delete</span> Delete
                </DropdownItem>
              </DropdownMenu>
            )}
          </DropdownContainer>
        </Header>

        {/* Main Content Canvas */}
        <MainContent>
          
          {/* Left Panel: Available Items */}
          <Panel>
            <PanelHeader>
              <PanelTitle>Available Items</PanelTitle>
              <Badge>{availableItems.length}</Badge>
            </PanelHeader>
            <ItemList>
              {loading ? (
                <EmptyState>
                  <span className="material-symbols-outlined">hourglass_empty</span>
                  <p>Loading items…</p>
                </EmptyState>
              ) : error ? (
                <EmptyState>
                  <span className="material-symbols-outlined">error</span>
                  <p>{error}</p>
                </EmptyState>
              ) : availableItems.length === 0 ? (
                <EmptyState>
                  <span className="material-symbols-outlined">inventory_2</span>
                  <p>No items available</p>
                </EmptyState>
              ) : (
                availableItems.map(item => (
                  <RowCard key={item.id} onClick={() => selectItem(item)}>
                    <FlexGroup propsGap={theme.spacing.md}>
                      <TextBlock>
                        <p className="title">{item.name}</p>
                        <p className="subtitle">Asset #{item.id}</p>
                      </TextBlock>
                    </FlexGroup>
                    <ActionButton onClick={(e) => { e.stopPropagation(); selectItem(item); }}>
                      <span className="material-symbols-outlined">add</span>
                    </ActionButton>
                  </RowCard>
                ))
              )}
            </ItemList>
          </Panel>

          {/* Right Panel: Selected Items */}
          <Panel>
            <PanelHeader backgroundColor={theme.colors.surfaceContainerLow}>
              <PanelTitle>Selected Items</PanelTitle>
              <Badge backgroundColor={theme.colors.primary} color={theme.colors.white} isPill>
                {selectedItems.length}
              </Badge>
            </PanelHeader>
            
            <ItemList>
              {selectedItems.length === 0 ? (
                <EmptyState>
                  <span className="material-symbols-outlined">playlist_add</span>
                  <p>No items selected yet</p>
                </EmptyState>
              ) : (
                selectedItems.map(item => (
                  <RowCard key={item.id} isActive onClick={() => deselectItem(item)}>
                    <FlexGroup propsGap={theme.spacing.md}>
                      <TextBlock>
                        <p className="title">{item.name}</p>
                        <p className="subtitle">Asset #{item.id}</p>
                      </TextBlock>
                    </FlexGroup>

                    <ActionButton isRemove onClick={(e) => { e.stopPropagation(); deselectItem(item); }}>
                      <span className="material-symbols-outlined">remove</span>
                    </ActionButton>

                  </RowCard>
                ))
              )}
            </ItemList>
            
            <FooterPanel>
              <SubmitButton onClick={handleSubmit} disabled={submitting}>
                <span className="material-symbols-outlined">send</span>
                <span>{submitting ? 'Submitting…' : 'Submit'}</span>
              </SubmitButton>
            </FooterPanel>
          </Panel>

        </MainContent>

        {/* Overlay Success Confirmation Modal */}
        {showSuccess && (
          <Overlay>
            <ModalCard>
              <SuccessIconWrapper>
                <span className="material-symbols-outlined">check_circle</span>
              </SuccessIconWrapper>
              <PanelTitle style={{ marginBottom: '8px' }}>Selection Confirmed</PanelTitle>
              <p style={{ color: theme.colors.onSurfaceVariant, marginBottom: '24px' }}>
                Your items have been successfully submitted to the system.
              </p>
              <SubmitButton onClick={() => setShowSuccess(false)}>Done</SubmitButton>
            </ModalCard>
          </Overlay>
        )}

      </RootWrapper>
    </ThemeProvider>
  );
}